import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import useSelectedRadio from '../hooks/useSelectedRadio';

const METRICS = [
  ['metric_active_listeners', 'Active listeners'],
  ['metric_listeners', 'Listeners'],
  ['metric_devices_os', 'Devices & OS'],
  ['metric_geo', 'Geo'],
  ['metric_peak', 'Peak'],
  ['metric_aha', 'AHA'],
  ['metric_tsl', 'TSL'],
  ['metric_tlh', 'TLH'],
  ['metric_new_listeners', 'New listeners'],
  ['metric_adr', 'ADR'],
  ['metric_aqh', 'AQH'],
  ['metric_reach', 'Reach'],
  ['metric_retention', 'Retention'],
  ['metric_loyalty', 'Loyalty'],
  ['metric_top', 'TOP'],
  ['metric_ltr', 'LTR'],
  ['metric_quartiles_ltr', 'Quartiles LTR'],
  ['metric_ads', 'Ads'],
];

const emptySettings = {
  portal_enabled: false,
  metric_active_listeners: false,
  metric_listeners: false,
  metric_devices_os: false,
  metric_geo: false,
  metric_peak: false,
  metric_aha: false,
  metric_tsl: false,
  metric_tlh: false,
  metric_new_listeners: false,
  metric_adr: false,
  metric_aqh: false,
  metric_reach: false,
  metric_retention: false,
  metric_loyalty: false,
  metric_top: false,
  metric_ltr: false,
  metric_quartiles_ltr: false,
  metric_ads: false,
};

export default function RadioPortalPage() {
  const { t } = useTranslation('common');
  const { shortcode, name: stationName } = useSelectedRadio();

  const [radioId, setRadioId] = useState(null);
  const [settings, setSettings] = useState(emptySettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API = useMemo(() => (API_BASE_URL || '').replace(/\/+$/, ''), []);

  const loadSettings = useCallback(async () => {
    if (!shortcode) {
      setRadioId(null);
      setSettings(emptySettings);
      setError('');
      setSuccess('');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const radiosResp = await authFetch(`${API}/radios`);
      const radiosJson = await radiosResp.json();

      if (!radiosResp.ok || !Array.isArray(radiosJson)) {
        throw new Error('Не вдалося отримати список станцій');
      }

      const currentRadio = radiosJson.find((item) => item.shortcode === shortcode);

      if (!currentRadio?.id) {
        throw new Error('Не вдалося визначити поточну станцію');
      }

      setRadioId(currentRadio.id);

      const settingsResp = await authFetch(`${API}/radios/${currentRadio.id}/portal-settings`);
      const settingsJson = await settingsResp.json();

      if (!settingsResp.ok) {
        throw new Error(settingsJson?.detail || 'Не вдалося завантажити налаштування порталу');
      }

      setSettings({
        portal_enabled: !!settingsJson.portal_enabled,
        metric_active_listeners: !!settingsJson.metric_active_listeners,
        metric_listeners: !!settingsJson.metric_listeners,
        metric_devices_os: !!settingsJson.metric_devices_os,
        metric_geo: !!settingsJson.metric_geo,
        metric_peak: !!settingsJson.metric_peak,
        metric_aha: !!settingsJson.metric_aha,
        metric_tsl: !!settingsJson.metric_tsl,
        metric_tlh: !!settingsJson.metric_tlh,
        metric_new_listeners: !!settingsJson.metric_new_listeners,
        metric_adr: !!settingsJson.metric_adr,
        metric_aqh: !!settingsJson.metric_aqh,
        metric_reach: !!settingsJson.metric_reach,
        metric_retention: !!settingsJson.metric_retention,
        metric_loyalty: !!settingsJson.metric_loyalty,
        metric_top: !!settingsJson.metric_top,
        metric_ltr: !!settingsJson.metric_ltr,
        metric_quartiles_ltr: !!settingsJson.metric_quartiles_ltr,
        metric_ads: !!settingsJson.metric_ads,
      });
    } catch (e) {
      setRadioId(null);
      setSettings(emptySettings);
      setError(e.message || 'Не вдалося завантажити налаштування порталу');
    } finally {
      setLoading(false);
    }
  }, [API, shortcode]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onTogglePortal = (e) => {
    const checked = e.target.checked;
    setSettings((prev) => ({ ...prev, portal_enabled: checked }));
    setSuccess('');
  };

  const onToggleMetric = (key) => (e) => {
    const checked = e.target.checked;
    setSettings((prev) => ({ ...prev, [key]: checked }));
    setSuccess('');
  };

  const onSave = async () => {
    if (!radioId) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const resp = await authFetch(`${API}/radios/${radioId}/portal-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });

      const json = await resp.json();

      if (!resp.ok) {
        throw new Error(json?.detail || 'Не вдалося зберегти налаштування порталу');
      }

      setSettings({
        portal_enabled: !!json.portal_enabled,
        metric_active_listeners: !!json.metric_active_listeners,
        metric_listeners: !!json.metric_listeners,
        metric_devices_os: !!json.metric_devices_os,
        metric_geo: !!json.metric_geo,
        metric_peak: !!json.metric_peak,
        metric_aha: !!json.metric_aha,
        metric_tsl: !!json.metric_tsl,
        metric_tlh: !!json.metric_tlh,
        metric_new_listeners: !!json.metric_new_listeners,
        metric_adr: !!json.metric_adr,
        metric_aqh: !!json.metric_aqh,
        metric_reach: !!json.metric_reach,
        metric_retention: !!json.metric_retention,
        metric_loyalty: !!json.metric_loyalty,
        metric_top: !!json.metric_top,
        metric_ltr: !!json.metric_ltr,
        metric_quartiles_ltr: !!json.metric_quartiles_ltr,
        metric_ads: !!json.metric_ads,
      });

      setSuccess('Налаштування збережено');
    } catch (e) {
      setError(e.message || 'Не вдалося зберегти налаштування порталу');
    } finally {
      setSaving(false);
    }
  };

  if (!shortcode) {
    return (
      <section style={pageStyle}>
        <h1 style={titleStyle}>{t('nav.cabinet.radio_portal')}</h1>
        <div style={noticeStyle}>Оберіть станцію у верхньому списку.</div>
      </section>
    );
  }

  return (
    <section style={pageStyle}>
      <h1 style={titleStyle}>{t('nav.cabinet.radio_portal')}</h1>

      <div style={leadStyle}>
        Налаштування передачі даних у Radio Portal для станції <strong>{stationName || shortcode}</strong>.
      </div>

      {loading && <div style={noticeStyle}>Завантаження...</div>}
      {!!error && !loading && <div style={errorStyle}>{error}</div>}
      {!!success && !loading && <div style={successStyle}>{success}</div>}

      {!loading && !error && (
        <>
          <div style={masterRowStyle}>
            <label style={toggleLabelStyle}>
              <input
                type="checkbox"
                checked={settings.portal_enabled}
                onChange={onTogglePortal}
              />
              <span style={{ marginLeft: 10 }}>
                Передавати дані цієї станції в Radio Portal
              </span>
            </label>
          </div>

          <div style={metricsWrapStyle}>
            {METRICS.map(([key, label]) => (
              <label
                key={key}
                style={{
                  ...metricRowStyle,
                  opacity: settings.portal_enabled ? 1 : 0.5,
                  cursor: settings.portal_enabled ? 'pointer' : 'not-allowed',
                }}
              >
                <span>{label}</span>
                <input
                  type="checkbox"
                  checked={settings[key]}
                  onChange={onToggleMetric(key)}
                  disabled={!settings.portal_enabled}
                />
              </label>
            ))}
          </div>

          <div style={actionsStyle}>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              style={{
                ...saveButtonStyle,
                opacity: saving ? 0.7 : 1,
                cursor: saving ? 'default' : 'pointer',
              }}
            >
              {saving ? 'Збереження...' : 'Зберегти'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

const pageStyle = {
  width: '100%',
  minHeight: '60vh',
  background: '#fff',
  borderRadius: 12,
  padding: '32px 36px',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  boxShadow: '0 2px 18px rgba(0,0,0,0.05)',
};

const titleStyle = {
  margin: 0,
  fontSize: 28,
  fontWeight: 600,
};

const leadStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  background: '#f8f8f8',
  color: '#1a1a1a',
  fontSize: 16,
  boxSizing: 'border-box',
};

const noticeStyle = {
  padding: '14px 16px',
  borderRadius: 8,
  background: '#f7f7f7',
  color: '#333',
  fontSize: 16,
};

const errorStyle = {
  padding: '14px 16px',
  borderRadius: 8,
  background: '#fff2f2',
  color: '#c62828',
  fontSize: 16,
};

const successStyle = {
  padding: '14px 16px',
  borderRadius: 8,
  background: '#eefaf0',
  color: '#1f7a35',
  fontSize: 16,
};

const masterRowStyle = {
  padding: '18px 20px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  background: '#fafafa',
};

const toggleLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  fontSize: 17,
  fontWeight: 600,
};

const metricsWrapStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 12,
};

const metricRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '14px 16px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  background: '#fff',
  fontSize: 15,
};

const actionsStyle = {
  display: 'flex',
  justifyContent: 'flex-start',
};

const saveButtonStyle = {
  background: '#3867fa',
  color: '#fff',
  border: 'none',
  padding: '10px 18px',
  borderRadius: 8,
  fontWeight: 600,
  fontSize: 15,
};